// Cart drawer per-line delivery dates.
//
// One batched checkServiceability request carries every distinct SKU in the cart
// and each line's "Delivery by …" row is filled from the answer. The API takes an
// items[] array, so the number of lines does not change the number of requests.
//
// A line with no date simply shows no row. That is deliberate, and it fails safe:
// "SKU unknown to Omuni", "OMS token expired" and "we do not deliver there" are
// indistinguishable to this caller, so a backend outage quietly drops the rows
// rather than telling every shopper their order is undeliverable. A delivery
// verdict never blocks checkout.

// Answers keyed per (pincode, sku) rather than per cart, so a quantity edit or a
// removed line repaints straight from cache and only a genuinely new SKU costs a
// request. Module scope because the element is destroyed and rebuilt on every
// cart write — anything held on the instance would be thrown away each time.
const SERVICEABILITY_RESULTS = new Map();

// Batches in flight, so the connect-then-refresh double call (and two fast cart
// writes) share one request instead of racing two.
const SERVICEABILITY_INFLIGHT = new Map();

const SERVICEABILITY_PINCODE_LENGTH = 6;

const serviceabilityResultKey = (pincode, sku) => `${pincode}|${sku}`;

// Back/forward restore replays the JS heap, so a cache built before the shopper
// navigated away would be served without ever being re-checked. Delivery dates go
// stale by the day — drop them and let the next render re-ask.
window.addEventListener("pageshow", (event) => {
  if (event.persisted) SERVICEABILITY_RESULTS.clear();
});

class CartServiceability extends HTMLElement {
  connectedCallback() {
    const { endpoint, brandSlug, deliveryPrefix, undeliverablePrefix } =
      this.dataset;
    this.endpoint = endpoint;
    this.brandSlug = brandSlug;
    this.deliveryPrefix = deliveryPrefix || "Delivery by";
    this.undeliverablePrefix = undeliverablePrefix || "Not deliverable to";

    // The pincode can change while the cart sits untouched — the shopper checks a
    // different one on a PDP — and nothing about that is a cart write, so neither a
    // render nor a reconnect would happen. Without this the cart keeps the verdicts
    // it resolved at page load and checkout stays open on lines we can no longer
    // deliver.
    this.onPincodeChecked = () => this.refresh();
    document.addEventListener("pincode:checked", this.onPincodeChecked);

    // Same change made in another tab. localStorage only fires `storage` in the
    // tabs that did NOT write it, so this never double-fires with the above.
    this.onStorage = (event) => {
      if (event.key === null || event.key === window.PincodeStorage?.key) {
        this.refresh();
      }
    };
    window.addEventListener("storage", this.onStorage);

    this.refresh();
  }

  disconnectedCallback() {
    document.removeEventListener("pincode:checked", this.onPincodeChecked);
    window.removeEventListener("storage", this.onStorage);
  }

  // Public. Runs on connect — which covers first paint and every cart write, since
  // [data-cart-extras] is re-rendered and this element rebuilt with it — and again
  // from cart-drawer.js after a render, so the fill does not silently depend on
  // [data-cart-items] being swapped before [data-cart-extras].
  refresh() {
    // The pincode is captured on the PDP — by the delivery check, or by the
    // add-to-cart gate — and only read here. The cart never asks for one.
    const pincode = window.PincodeStorage?.get() ?? "";
    if (pincode) this.fill(pincode);
  }

  // The drawer, not the document. A product's markup can exist in more than one
  // place at once, and a document-wide lookup would write a cart line's delivery
  // date onto an element outside the cart.
  root() {
    return this.closest("cart-drawer") ?? this.closest("[data-cart-page]") ?? this;
  }

  lines() {
    return [...this.root().querySelectorAll("[data-line-sku]")]
      .map((row) => ({
        row,
        sku: row.dataset.lineSku,
        quantity: Number(row.dataset.lineQuantity) || 1,
      }))
      .filter((line) => line.sku);
  }

  async fill(pincode) {
    const lines = this.lines();
    if (!lines.length) return;

    // A SKU can sit on more than one line: Shopify merges only identical lines, so
    // the same variant with different line properties stays separate. Ask once
    // with the quantities summed, then fan the single answer back out to every
    // line that shares it.
    const quantityBySku = new Map();
    for (const { sku, quantity } of lines) {
      quantityBySku.set(sku, (quantityBySku.get(sku) ?? 0) + quantity);
    }

    // Anything already answered paints immediately, so a quantity edit redraws its
    // date in the same frame as the rest of the line.
    this.paint(lines, pincode);

    const missing = [...quantityBySku]
      .filter(([sku]) => !SERVICEABILITY_RESULTS.has(serviceabilityResultKey(pincode, sku)))
      .map(([skuId, quantity]) => ({ skuId, quantity }));
    if (!missing.length || !this.endpoint) return;

    try {
      await this.load(pincode, missing);
    } catch (error) {
      // Silent by design — see the file header. There is no pincode field here to
      // send the shopper back to, and a failed check is never a delivery verdict.
      console.error("[cart-serviceability] check failed:", error);
      return;
    }

    // Re-read the rows: a cart write may have replaced them while the request was
    // in flight, leaving the originals detached from the document.
    this.paint(this.lines(), pincode);
  }

  load(pincode, items) {
    const batchKey = `${pincode}|${items
      .map((item) => item.skuId)
      .sort()
      .join(",")}`;
    const existing = SERVICEABILITY_INFLIGHT.get(batchKey);
    if (existing) return existing;

    const request = window.ServiceabilityApi.check({
      endpoint: this.endpoint,
      brandSlug: this.brandSlug,
      pincode,
      items,
    })
      .then((rows) => {
        const bySku = new Map(rows.map((row) => [row.skuId, row]));
        // Misses are cached too, so a SKU the backend has no row for is not asked
        // about again on every render. Only reached when the request itself
        // succeeded: a thrown request caches nothing and is retried next time.
        for (const { skuId } of items) {
          SERVICEABILITY_RESULTS.set(
            serviceabilityResultKey(pincode, skuId),
            bySku.get(skuId) ?? null,
          );
        }
      })
      .finally(() => {
        SERVICEABILITY_INFLIGHT.delete(batchKey);
      });

    SERVICEABILITY_INFLIGHT.set(batchKey, request);
    return request;
  }

  paint(lines, pincode) {
    for (const { row, sku } of lines) {
      const result = SERVICEABILITY_RESULTS.get(
        serviceabilityResultKey(pincode, sku),
      );

      // The checkout block, from an authoritative `serviceable: false` only — a
      // thrown request never stamps, so a backend outage cannot close checkout for
      // the whole store. cart-drawer.js and cart.js read this; see
      // _syncCheckoutAvailability.
      //
      // Only touched when there IS an answer. A pincode change repaints before the
      // new verdicts land, and clearing the stamp on the way past would reopen
      // checkout for the width of the request — on lines we had just established we
      // cannot deliver. A stale block is the safe side of that race.
      if (result !== undefined) {
        row.toggleAttribute(
          "data-line-unserviceable",
          result?.serviceable === false,
        );
      }

      const slot = row.querySelector("[data-line-edd]");
      const textEl = row.querySelector("[data-line-edd-text]");
      const blockedSlot = row.querySelector("[data-line-undeliverable]");
      const blockedTextEl = row.querySelector("[data-line-undeliverable-text]");

      // An authoritative refusal is stated on the line, not hidden. It is the
      // only thing explaining why checkout is closed, and the shopper can act on
      // it — everything else (no answer yet, unknown SKU, a failed request) still
      // shows nothing rather than guessing at a reason.
      if (result?.serviceable === false) {
        if (blockedSlot && blockedTextEl) {
          blockedTextEl.textContent = `${this.undeliverablePrefix} ${pincode}`;
          blockedSlot.hidden = false;
        }
        if (slot) slot.hidden = true;
        continue;
      }
      if (blockedSlot) blockedSlot.hidden = true;

      if (!slot || !textEl) continue;

      const deliveryTime = result?.serviceable
        ? window.ServiceabilityApi?.soonestDeliveryTime(result.deliveryOptions)
        : null;

      if (deliveryTime === null || deliveryTime === undefined) {
        slot.hidden = true;
        continue;
      }

      textEl.textContent = `${this.deliveryPrefix} ${window.ServiceabilityApi.formatDeliveryDate(deliveryTime)}`;
      slot.hidden = false;
    }

    this.announce();
  }

  // Tells whichever cart surface encloses this element to re-judge checkout, and
  // surfaces the reason it is closed. Bubbling CustomEvent rather than reaching
  // into the drawer's methods, because the cart page has no object to call.
  announce() {
    this.dispatchEvent(
      new CustomEvent("serviceability:changed", { bubbles: true }),
    );
  }

}

if (!customElements.get("cart-serviceability")) {
  customElements.define("cart-serviceability", CartServiceability);
}
